import { clubPath, clubUrl } from '../clubhouse';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Leaf, Flag, UtensilsCrossed, Settings, LogOut, ChevronRight, Hotel } from 'lucide-react';

const ModuleSelector = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const modules = [
    {
      id: 'entretien',
      name: 'Entretien',
      description: 'Gestion terrain',
      icon: Leaf,
      gradient: 'from-emerald-500 to-green-600',
      shadowColor: 'shadow-emerald-500/25',
      path: '/entretien'
    },
    {
      id: 'golf',
      name: 'Golf',
      description: 'Activités golf',
      icon: Flag,
      gradient: 'from-blue-500 to-indigo-600',
      shadowColor: 'shadow-blue-500/25',
      path: '/golf'
    },
    {
      id: 'restaurant',
      name: 'Restaurant',
      description: 'Gestion resto',
      icon: UtensilsCrossed,
      gradient: 'from-orange-500 to-red-500',
      shadowColor: 'shadow-orange-500/25',
      path: '/restaurant'
    },
    {
      id: 'kempferhof',
      name: 'Kempferhof',
      description: 'Compte de résultat',
      icon: Hotel,
      gradient: 'from-amber-500 to-yellow-700',
      shadowColor: 'shadow-amber-600/30',
      path: '/kempferhof'
    }
  ];
  
  // Filtrer les modules accessibles à l'utilisateur — règle STRICTE basée sur user.modules.
  // Aucun bypass : un admin sans 'kempferhof' dans ses modules ne voit pas la carte.
  const userModules = user?.modules || [];
  const isAdmin = !!(user?.is_super_admin || user?.user_role === 'admin' || user?.role === 'admin' || user?.role === 'super_admin'
    || (user?.email || '').toLowerCase() === 'aa@bassmanager.com');
  const accessibleModules = modules.filter(m => userModules.includes(m.id));

  // Auto-redirect si un seul module accessible
  useEffect(() => {
    if (accessibleModules.length === 1) {
      navigate(accessibleModules[0].path, { replace: true });
    }
  }, [accessibleModules, navigate]);

  return (
    <div className="min-h-screen h-[100dvh] bg-[#0a1f1a] flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.1),transparent_50%)]" />
      
      {/* Header compact */}
      <div className="relative z-10 pt-6 pb-4 px-6 text-center">
        <div className="w-14 h-14 rounded-2xl overflow-hidden mx-auto mb-3 bg-white/10 backdrop-blur-sm border border-white/20 shadow-xl">
          <img src="/bass-logo.png" alt="BASS" className="w-full h-full object-cover" />
        </div>
        <h1 className="text-2xl font-bold text-white tracking-tight">BASS GOLF</h1>
        <p className="text-[10px] text-emerald-400/80 uppercase tracking-[0.2em] font-medium">management</p>
      </div>

      {/* Welcome message */}
      <div className="relative z-10 text-center px-6 mb-6">
        <p className="text-white/60 text-sm">
          Bienvenue, <span className="text-white font-medium">{user?.first_name}</span>
        </p>
      </div>

      {/* Modules Grid */}
      <div className="relative z-10 flex-1 px-4 overflow-y-auto">
        <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
          {accessibleModules.map((module) => {
            const Icon = module.icon;
            return (
              <button
                key={module.id}
                onClick={() => navigate(module.path)}
                className="group relative"
                data-testid={`module-${module.id}`}
              >
                {/* Card */}
                <div className={`
                  relative overflow-hidden rounded-2xl p-4 
                  bg-gradient-to-br ${module.gradient}
                  shadow-lg ${module.shadowColor}
                  transform transition-all duration-300
                  group-hover:scale-105 group-hover:shadow-xl
                  group-active:scale-95
                `}>
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Icon */}
                  <div className="relative flex flex-col items-center">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2">
                      <Icon size={22} className="text-white" strokeWidth={2} />
                    </div>
                    <h3 className="text-white font-semibold text-sm text-center">{module.name}</h3>
                    <p className="text-white/70 text-[10px] mt-0.5 text-center">{module.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Administration : gestion des utilisateurs (comptes administrateurs uniquement) */}
        {isAdmin && (
          <div className="max-w-md mx-auto mt-6">
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group"
              data-testid="admin-users-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
                  <Settings size={18} className="text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium text-sm">Administration</p>
                  <p className="text-white/50 text-xs">Gérer les utilisateurs</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-white/40 group-hover:text-white/70 group-hover:translate-x-1 transition-all" />
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 py-4 px-6">
        <button
          onClick={() => {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = clubPath('/login');
          }}
          className="flex items-center justify-center gap-2 mx-auto text-white/40 hover:text-white/70 transition-colors text-sm py-2 px-4 rounded-xl hover:bg-white/5"
        >
          <LogOut size={16} />
          <span>Déconnexion</span>
        </button>
      </div>
    </div>
  );
};

export default ModuleSelector;
